import React, { useEffect, useState } from 'react'
import { Button, Grid, Heading, Icon, Text, useIsDesignLanguage, DesignLanguage, useDesignLanguagePerClass } from '@afterpaytouch/core'

import { Head, ModuleContainer } from '../../../src/component'
import { BrowserType, CardScanAmplitudeErrorReason, CardScanError, CheckoutError, CheckoutPage } from '../../../src/model'
import { Route, useSummaryRoute } from '../../../src/config/router'
import { PaymentMethodCard } from '../../../src/component/PaymentMethodCard'
import {
  initialiseScanIntent,
  setDisplayProcessingModal,
  startCardScan,
  useAppDispatch,
  useAppRouter,
  useIsReadyToScan,
  useSelectedCard,
  useConfirmCheckoutArgs,
  useConfirmCheckoutMutation,
} from '../../../src/state'
import { useConfirmCheckoutFinaliser } from '../../../src/state/checkout/useConfirmCheckoutFinaliser'
import { isMobileChrome, isMobileSafari, isMobileSamsung } from '../../../src/utils/device'
import { TrackingEvent } from '../../../src/model/monitoring'
import { capture } from '@afterpaytouch/integrations/monitoring'
import { TrackOnMount, useMonitoringWithEnduringEventProperties } from '../../../src/integrations/monitoring'
import { useTranslation } from '../../utils/convergence'
import { getCardScanSubtitle, getCardScanTitle } from './i18n'
import styles from './style.module.css'

const errorExist = (error: CardScanError | undefined): error is CardScanError => Boolean(error)
const isNonScanError = (error: CardScanError | undefined): boolean => {
  return error === CardScanError.Unsupported || error === CardScanError.NoPermission
}

const isScanAgainError = (error: CardScanError | undefined): boolean => {
  return error === CardScanError.DidntMatch || error === CardScanError.CannotScan
}

const isTrue = (v: unknown): v is true => v === true

export const CardScanPage: CheckoutPage = () => {
  const isArcadeDesignLanguage = useIsDesignLanguage(DesignLanguage.Arcade)
  const designLanguageStyles = useDesignLanguagePerClass(styles)

  const dispatch = useAppDispatch()
  const [confirmCheckoutMutation] = useConfirmCheckoutMutation()
  const confirmArgs = useConfirmCheckoutArgs()
  const [error, setError] = useState<CardScanError>()
  const [browserType, setBrowserType] = useState<BrowserType>(BrowserType.Others)
  const [clicked, setClicked] = useState<boolean>(false)
  const { t } = useTranslation()
  const router = useAppRouter()
  const ready = useIsReadyToScan()
  const selectedCard = useSelectedCard()
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const summaryRoute = useSummaryRoute()
  const pageTitle = `${t('cardScan:title')} | ${t('common:brandName')}`
  const finaliseConfirmWithHandledErrors = useConfirmCheckoutFinaliser({
    t,
    isTrusted: true,
    oAuthAuthorization: async () => {},
    shouldUpdatePreferredPaymentDay: false,
    returnRoute: summaryRoute,
    resetCheckoutErrorOnToast: false,
    setCardScanCheckpointOnRoute: false,
    onBeforeNavigate: () => setClicked(false),
    execute: async () => {
      try {
        const confirmResponse = await confirmCheckoutMutation(confirmArgs).unwrap()
        if (confirmResponse.status === 'FAILURE') {
          router.push(Route.TERMINAL_ERROR)
        } else {
          dispatch(setDisplayProcessingModal(true))
        }
      } catch (e) {
        // wont trigger card scan twice
        capture.error('Error during payment confirmation', { ...e })
        throw e
      }
    },
  })

  const title = getCardScanTitle({ t, error })
  const subtitle = getCardScanSubtitle({
    t,
    error,
    browserType,
    lastFour: selectedCard?.maskedPan.slice(-4),
  })
  const backToReviewOrderBtnVisible = !isNonScanError(error)
  const scanAgainBtnVisible = isScanAgainError(error)

  const handleBackToReviewOrder = (): void => {
    router.push(summaryRoute)
  }

  const initialise = async (): Promise<void> => {
    try {
      const initialiseScanIntentResult: true | CheckoutError = await dispatch(initialiseScanIntent())
      if (!isTrue(initialiseScanIntentResult) && initialiseScanIntentResult.error === CardScanError.Unsupported) {
        setError(CardScanError.Unsupported)
      }
    } catch (ignore) {
      router.push(Route.TERMINAL_ERROR)
    }
  }

  const handleScanClick = async (): Promise<void> => {
    setClicked(true)
    logEvent(TrackingEvent.PRESSED_SCAN_PAYMENT)
    const result: undefined | true | CheckoutError = await dispatch(startCardScan())

    if (isTrue(result)) {
      logEvent(TrackingEvent.SCAN_PAYMENT_MATCHED)
      await finaliseConfirmWithHandledErrors()
    } else {
      setError(result?.error as CardScanError)
      switch (result?.error) {
        case CardScanError.UserCanceled: // just remove the card-scan verify screen
          setError(undefined)
          break
        case CardScanError.DidntMatch:
          logEvent(TrackingEvent.SCAN_PAYMENT_FAILED, {
            reason: CardScanAmplitudeErrorReason.NotMatched,
          })
          initialise()
          break
        case CardScanError.TooManyTries:
          logEvent(TrackingEvent.SCAN_PAYMENT_FAILED, {
            reason: CardScanAmplitudeErrorReason.RateLimit,
          })
          break
        case CardScanError.NoPermission:
        case CardScanError.CannotScan:
          logEvent(TrackingEvent.SCAN_PAYMENT_FAILED, {
            reason: CardScanAmplitudeErrorReason.OtherReasons,
          })
          break
        case CardScanError.TerminalError:
          setError(undefined)
          logEvent(TrackingEvent.SCAN_PAYMENT_FAILED, {
            reason: CardScanAmplitudeErrorReason.OtherReasons,
          })
          router.push(Route.TERMINAL_ERROR)
          break
      }
    }
    setClicked(false)
  }

  useEffect(() => {
    initialise()
    if (isMobileSamsung()) {
      setBrowserType(BrowserType.Samsung)
    } else if (isMobileChrome()) {
      setBrowserType(BrowserType.Chrome)
    } else if (isMobileSafari()) {
      setBrowserType(BrowserType.Safari)
    }
  }, [])

  const subtitleColor = isArcadeDesignLanguage ? 'Subtle' : undefined

  return (
    <>
      <Head title={pageTitle} />
      <Grid>
        <Heading size='XXL'>{title}</Heading>
      </Grid>
      <Grid>
        <Text size='M' color={subtitleColor}>
          {subtitle}
        </Text>
      </Grid>
      {!errorExist(error) || isArcadeDesignLanguage ? (
        <>
          {selectedCard != null && (
            <ModuleContainer
              padding={{ vertical: 'Large', horizontal: 'XLarge' }}
              data-dd-action-name='Card Scan Selected Card'
              className={designLanguageStyles.moduleContainer}
            >
              <PaymentMethodCard card={selectedCard} errorKey={selectedCard?.error ?? undefined} />
            </ModuleContainer>
          )}
          <Grid>
            <Text size='M' color='Gray40' className={designLanguageStyles.tips}>
              {t('cardScan:tips')}
            </Text>
          </Grid>
          {errorExist(error) && isArcadeDesignLanguage && (
            <>
              <Grid>
                <Grid.Item>
                  <Button.Secondary padding='Fluid' onClick={handleBackToReviewOrder} testNameSpace='cardScan'>
                    {t('cardScan:backToReviewOrder')}
                  </Button.Secondary>
                </Grid.Item>
              </Grid>
              <span className={designLanguageStyles.buttonSpacer}></span>
            </>
          )}
          <Grid>
            <Grid.Item>
              <Button padding='Fluid' onClick={handleScanClick} testNameSpace='cardScan' loading={clicked} disabled={!ready || clicked}>
                <Icon.Camera size='Small' title={t('cardScan:scan')} />
                {t('cardScan:scan')}
              </Button>
            </Grid.Item>
          </Grid>
        </>
      ) : (
        <>
          {backToReviewOrderBtnVisible && (
            <Grid>
              <Button padding='Fluid' onClick={handleBackToReviewOrder} testNameSpace='cardScan'>
                {t('cardScan:backToReviewOrder')}
              </Button>
            </Grid>
          )}
          {scanAgainBtnVisible && (
            <Grid>
              <Button.Ghost padding='Fluid' loading={clicked} onClick={handleScanClick} testNameSpace='cardScan' disabled={!ready || clicked}>
                <Icon.Camera size='Small' title={t('cardScan:scanAgain')} />
                {t('cardScan:scanAgain')}
              </Button.Ghost>
            </Grid>
          )}
        </>
      )}
      <TrackOnMount eventName={TrackingEvent.VIEWED_CARD_SCAN_PAGE} />
    </>
  )
}
